import { useCallback } from 'react'
import { AxiosResponse } from 'axios'
import { useMutation, useQuery, useQueryCache } from 'react-query'
import { getLatestDocumentVerification, requestDocumentVerification } from 'services/documentVerification'
import { DocumentVerificationStatus } from 'typescript/enums/documentVerification'
import { IClientDocumentVerification, IRequestDocumentVerificationParams } from 'typescript/interfaces/documentVerification'

const LATEST_DOCUMENT_VERIFICATION_QUERY_KEY = 'latestDocumentVerification'
const PENDING_POLL_INTERVAL = 3000
const RECOMPARE_START_DELAY = 2000

export const useLatestDocumentVerification = (clientId: string, enabled: boolean) => {
  const queryCache = useQueryCache()
  const queryKey = [LATEST_DOCUMENT_VERIFICATION_QUERY_KEY, clientId]
  const cached = queryCache.getQueryData<AxiosResponse<IClientDocumentVerification>>(queryKey)
  const isPending = cached?.data.status === DocumentVerificationStatus.pending

  const { data } = useQuery(
    queryKey,
    () => getLatestDocumentVerification(clientId),
    isPending ? { enabled, refetchInterval: PENDING_POLL_INTERVAL } : { enabled }
  )

  return {
    verification: data?.data,
  }
}

export const useRequestDocumentVerification = () => {
  const queryCache = useQueryCache()

  const [requestVerification] = useMutation(requestDocumentVerification, {
    onSuccess: (_, { clientId }: IRequestDocumentVerificationParams) => {
      queryCache.invalidateQueries([LATEST_DOCUMENT_VERIFICATION_QUERY_KEY, clientId])
    },
  })

  return {
    requestVerification,
  }
}

export const useRefreshDocumentVerification = () => {
  const queryCache = useQueryCache()

  const refreshVerification = useCallback(
    (clientId: string) => {
      const queryKey = [LATEST_DOCUMENT_VERIFICATION_QUERY_KEY, clientId]
      queryCache.invalidateQueries(queryKey)
      setTimeout(() => queryCache.invalidateQueries(queryKey), RECOMPARE_START_DELAY)
    },
    [queryCache]
  )

  return {
    refreshVerification,
  }
}
